import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'

import { ApiClientError } from '@/api/client'
import {
  createAdminAdmin,
  fetchAdminAdmins,
  sendAdminAdminPasswordReset,
  setAdminAdminActive,
  updateAdminAdmin,
  type AdminAccount,
  type AdminPasswordResetResult,
  type AdminUpsertPayload,
} from '@/api/adminsApi'
import { useAuth } from '@/auth/AuthContext'
import { AdminHeaderActions } from '@/components/admin/AdminHeaderActions'

import authStyles from './AuthPages.module.css'
import styles from './PortalPages.module.css'

const EMPTY_FORM: AdminUpsertPayload = {
  username: '',
  email: '',
  full_name: '',
  whatsapp_number: '',
  is_active: false,
}

function toForm(admin: AdminAccount): AdminUpsertPayload {
  return {
    username: admin.username,
    email: admin.email,
    full_name: admin.full_name,
    whatsapp_number: admin.whatsapp_number || '',
    is_active: admin.is_active,
  }
}

/** Admin page to create, edit, and invite other admin accounts. */
export function AdminAdminsPage() {
  const { isAuthenticated, isAdmin, logout, user } = useAuth()
  const [admins, setAdmins] = useState<AdminAccount[]>([])
  const [form, setForm] = useState<AdminUpsertPayload>(EMPTY_FORM)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')
  const [busy, setBusy] = useState(false)
  const [showInactive, setShowInactive] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [resetLink, setResetLink] = useState<AdminPasswordResetResult | null>(null)

  async function reload() {
    const result = await fetchAdminAdmins()
    setAdmins(result.admins)
  }

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) return
    void (async () => {
      try {
        await reload()
      } catch (err) {
        setError(err instanceof ApiClientError ? err.message : 'Could not load admins.')
      }
    })()
  }, [isAuthenticated, isAdmin])

  const { active, inactive } = useMemo(() => {
    const activeAdmins = admins.filter((admin) => admin.is_active)
    const inactiveAdmins = admins.filter((admin) => !admin.is_active)
    return { active: activeAdmins, inactive: inactiveAdmins }
  }, [admins])

  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!isAdmin) return <Navigate to="/vendor" replace />

  function updateField<K extends keyof AdminUpsertPayload>(key: K, value: AdminUpsertPayload[K]) {
    setForm((current) => ({ ...current, [key]: value }))
    setSaved('')
  }

  function resetForm() {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setShowForm(false)
    setSaved('')
  }

  function startCreate() {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setShowForm(true)
    setError('')
    setSaved('')
    setResetLink(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function startEdit(admin: AdminAccount) {
    setEditingId(admin.id)
    setForm(toForm(admin))
    setShowForm(true)
    setError('')
    setSaved('')
    setResetLink(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    setSaved('')
    setResetLink(null)
    try {
      if (editingId == null) {
        await createAdminAdmin(form)
        setSaved(
          'Admin created as inactive (cannot sign in yet). Send a password reset so they can set a password and activate.',
        )
        setShowInactive(true)
      } else {
        await updateAdminAdmin(editingId, {
          username: form.username,
          email: form.email,
          full_name: form.full_name,
          whatsapp_number: form.whatsapp_number,
          is_active: form.is_active,
        })
        setSaved('Admin account updated.')
      }
      await reload()
      resetForm()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save admin.')
    } finally {
      setBusy(false)
    }
  }

  async function onToggleActive(admin: AdminAccount) {
    if (user?.id === admin.id && admin.is_active) {
      setError('You cannot deactivate your own admin account while signed in.')
      return
    }
    setBusy(true)
    setError('')
    try {
      await setAdminAdminActive(admin.id, !admin.is_active)
      await reload()
      if (editingId === admin.id) {
        setForm((current) => ({ ...current, is_active: !admin.is_active }))
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not update admin status.')
    } finally {
      setBusy(false)
    }
  }

  async function onSendPasswordReset(admin: AdminAccount) {
    setBusy(true)
    setError('')
    setSaved('')
    setResetLink(null)
    try {
      const result = await sendAdminAdminPasswordReset(admin.id)
      if (result.emailed) {
        setSaved(`Password reset email sent to ${result.email}. Link expires in ${result.expires_hours}h.`)
      } else if (result.reset_url) {
        setResetLink(result)
        setSaved(
          `Email is not configured — copy the reset link below and send it to ${result.email} (expires in ${result.expires_hours}h).`,
        )
      } else {
        setSaved(`Password reset prepared for ${result.email}.`)
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not send password reset.')
    } finally {
      setBusy(false)
    }
  }

  async function copyResetUrl() {
    if (!resetLink?.reset_url) return
    try {
      await navigator.clipboard.writeText(resetLink.reset_url)
      setSaved('Reset link copied to clipboard.')
    } catch {
      setError('Could not copy — select the link and copy it manually.')
    }
  }

  function renderAdminList(items: AdminAccount[]) {
    if (items.length === 0) {
      return <p className={styles.empty}>No admins in this list.</p>
    }
    return items.map((admin) => (
      <article key={admin.id} className={styles.item}>
        <div>
          <h2 className={styles.itemTitle}>
            {admin.full_name}
            {user?.id === admin.id ? ' · you' : ''}
          </h2>
          <p className={styles.meta}>
            @{admin.username} · {admin.email}
            {admin.whatsapp_number ? ` · WA ${admin.whatsapp_number}` : ''}
          </p>
          <p className={styles.meta}>
            Status:{' '}
            <strong className={admin.is_active ? styles.metaOk : styles.metaAlert}>
              {admin.is_active ? 'active' : 'inactive'}
            </strong>
            {admin.created_at ? ` · joined ${admin.created_at.slice(0, 10)}` : ''}
          </p>
        </div>
        <div className={styles.actionCol}>
          <button type="button" className={styles.secondary} disabled={busy} onClick={() => startEdit(admin)}>
            Edit
          </button>
          <button
            type="button"
            className={styles.primary}
            disabled={busy}
            onClick={() => void onSendPasswordReset(admin)}
          >
            Send password reset
          </button>
          <button
            type="button"
            className={admin.is_active ? styles.danger : styles.ghost}
            disabled={busy || (user?.id === admin.id && admin.is_active)}
            onClick={() => void onToggleActive(admin)}
          >
            {admin.is_active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </article>
    ))
  }

  return (
    <section className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.brand}>Oikos Market Day</p>
          <h1 className={styles.title}>Admin · Admins</h1>
          <p className={styles.lead}>
            Create admin accounts (inactive until they set a password), send password resets, and
            activate or deactivate logins.
          </p>
        </div>
        <AdminHeaderActions logout={logout} />
      </header>

      {error ? <p className={styles.error}>{error}</p> : null}
      {saved ? <p className={styles.metaOk}>{saved}</p> : null}
      {resetLink?.reset_url ? (
        <div className={styles.item} style={{ marginBottom: '1rem' }}>
          <p className={styles.meta}>
            Reset link for <strong>{resetLink.email}</strong>:
          </p>
          <p className={styles.meta} style={{ wordBreak: 'break-all' }}>
            {resetLink.reset_url}
          </p>
          <div className={styles.actions}>
            <button type="button" className={styles.primary} onClick={() => void copyResetUrl()}>
              Copy link
            </button>
            <button type="button" className={styles.ghost} onClick={() => setResetLink(null)}>
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {showForm ? (
        <form className={styles.formGrid} onSubmit={(event) => void onSubmit(event)}>
          <h2 className={styles.itemTitle}>
            {editingId != null ? `Edit admin #${editingId}` : 'New admin account'}
          </h2>
          {editingId == null ? (
            <p className={styles.meta}>
              No password is set here. The account stays inactive until you send a password reset and the
              admin chooses a password.
            </p>
          ) : null}

          <label className={authStyles.label}>
            Full name
            <input
              className={authStyles.input}
              value={form.full_name}
              onChange={(e) => updateField('full_name', e.target.value)}
              required
            />
          </label>
          <label className={authStyles.label}>
            Username
            <input
              className={authStyles.input}
              value={form.username}
              onChange={(e) => updateField('username', e.target.value)}
              required
            />
          </label>
          <label className={authStyles.label}>
            Email
            <input
              className={authStyles.input}
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              required
            />
          </label>
          <label className={authStyles.label}>
            WhatsApp number (optional)
            <input
              className={authStyles.input}
              type="tel"
              value={form.whatsapp_number || ''}
              onChange={(e) => updateField('whatsapp_number', e.target.value)}
            />
          </label>
          {editingId != null ? (
            <label className={styles.check}>
              <input
                type="checkbox"
                checked={Boolean(form.is_active)}
                onChange={(e) => updateField('is_active', e.target.checked)}
                disabled={user?.id === editingId}
              />
              Account is active (can sign in)
            </label>
          ) : null}

          <div className={styles.actions}>
            <button className={authStyles.submit} type="submit" disabled={busy}>
              {busy ? 'Saving…' : editingId != null ? 'Save admin' : 'Create admin'}
            </button>
            <button type="button" className={styles.ghost} onClick={resetForm} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      <div className={styles.list} style={{ marginTop: showForm ? '1.5rem' : 0 }}>
        <div className={styles.header} style={{ marginBottom: '0.75rem' }}>
          <h2 className={styles.itemTitle}>Active admins ({active.length})</h2>
          <div className={styles.actions}>
            <button type="button" className={styles.primary} onClick={startCreate}>
              New admin
            </button>
            <button
              type="button"
              className={styles.secondary}
              onClick={() => setShowInactive((current) => !current)}
            >
              {showInactive ? 'Hide inactive' : `List inactive (${inactive.length})`}
            </button>
          </div>
        </div>
        {renderAdminList(active)}

        {showInactive ? (
          <>
            <h2 className={styles.itemTitle} style={{ marginTop: '1.5rem' }}>
              Inactive admins
            </h2>
            {renderAdminList(inactive)}
          </>
        ) : null}
      </div>
    </section>
  )
}
